import { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton } from "@mui/material";
import { StablesDepositSkeleton } from "../../../Components/Molecules/DashboardSkeleton";
import intl from "react-intl-universal";
import { useToast } from "../../../Hooks/toast";
import { useStablesBalances } from "../../../Hooks/useStablesBalances";
import { useStablesDeposit } from "../../../Hooks/useStablesDeposit";
import { StablesAssetSelect, StablesNetworkSelect } from "./components/StablesSelect";
import { StablesDepositAddressPanel } from "./components/StablesDepositAddressPanel";

/**
 * Shared deposit form (IB + BaaS).
 *
 * BaaS admin can lock asset/network/address via overrides (no client deposit APIs):
 * - assetsOverride / networksOverride / addressOverride / lockSelectors
 */
export const StablesDepositarContent = ({
  isActive = true,
  assetsOverride = null,
  networksOverride = null,
  addressOverride = null,
  lockSelectors = false,
  emptyAddressMessage = null,
  introMessage = null,
  initialAssetId = null,
}) => {
  const toast = useToast();
  const boundMode = Boolean(assetsOverride || networksOverride || addressOverride != null);

  const {
    assets: balanceAssets,
    loading: balancesLoading,
    error: balancesError,
    reload: reloadBalances,
  } = useStablesBalances({ enabled: !boundMode });

  const assets = assetsOverride?.length ? assetsOverride : balanceAssets;
  const defaultAssetId = initialAssetId && assets.some((a) => a.id === initialAssetId)
    ? initialAssetId
    : assets[0]?.id ?? "";
  const [selectedAssetId, setSelectedAssetId] = useState(defaultAssetId);
  const [selectedNetworkId, setSelectedNetworkId] = useState("");
  const [networkTouched, setNetworkTouched] = useState(false);

  const depositEnabled = isActive && !boundMode;

  const {
    availableNetworks: apiNetworks,
    preferredNetworkId,
    currentAddress: apiAddress,
    hasAddress: apiHasAddress,
    loading,
    generating,
    generateAddress,
    networksLoading,
    networksError,
    error: addressError,
    reloadNetworks,
    reloadAddress,
  } = useStablesDeposit({
    assetId: selectedAssetId,
    networkId: selectedNetworkId || undefined,
    enabled: depositEnabled,
  });

  const availableNetworks = networksOverride?.length ? networksOverride : apiNetworks;
  const currentAddress = boundMode
    ? String(addressOverride ?? "").trim()
    : apiAddress;
  const hasAddress = boundMode ? Boolean(currentAddress) : apiHasAddress;

  useEffect(() => {
    if (assets.length && !assets.some((asset) => asset.id === selectedAssetId)) {
      setSelectedAssetId(assets[0].id);
    }
  }, [assets, selectedAssetId]);

  useEffect(() => {
    if (!isActive && !lockSelectors) {
      const resetAsset =
        initialAssetId && assets.some((a) => a.id === initialAssetId)
          ? initialAssetId
          : assets[0]?.id ?? "";
      setSelectedAssetId(resetAsset);
      setSelectedNetworkId("");
      setNetworkTouched(false);
    }
  }, [isActive, assets, lockSelectors, initialAssetId]);

  // Auto-set network from preferred wallet/deposit address for the asset.
  useEffect(() => {
    if (boundMode || lockSelectors || networkTouched || loading) return;
    if (!preferredNetworkId) return;
    if (selectedNetworkId === preferredNetworkId) return;
    if (
      availableNetworks.length &&
      !availableNetworks.some((n) => n.id === preferredNetworkId)
    ) {
      return;
    }
    setSelectedNetworkId(preferredNetworkId);
  }, [
    boundMode,
    lockSelectors,
    networkTouched,
    loading,
    preferredNetworkId,
    selectedNetworkId,
    availableNetworks,
  ]);

  // Bound / locked mode: keep override network selected.
  useEffect(() => {
    if (!lockSelectors && !boundMode) return;
    if (availableNetworks.length && !availableNetworks.some((n) => n.id === selectedNetworkId)) {
      setSelectedNetworkId(availableNetworks[0]?.id ?? "");
    }
  }, [availableNetworks, selectedNetworkId, lockSelectors, boundMode]);

  const selectedAsset = useMemo(
    () => assets.find((asset) => asset.id === selectedAssetId),
    [assets, selectedAssetId]
  );
  const selectedNetwork = useMemo(
    () => availableNetworks.find((network) => network.id === selectedNetworkId),
    [availableNetworks, selectedNetworkId]
  );

  const handleCopy = useCallback(
    async (value) => {
      try {
        await navigator.clipboard.writeText(value);
        toast.addToast({
          type: "success",
          title: intl.get("stables.depositCopiedTitle"),
          description: intl.get("stables.depositCopiedDescription"),
        });
      } catch {
        toast.addToast({
          type: "error",
          title: intl.get("stables.depositCopyErrorTitle"),
          description: intl.get("stables.depositCopyErrorDescription"),
        });
      }
    },
    [toast]
  );

  const handleGenerateAddress = useCallback(async () => {
    if (boundMode) return;
    const result = await generateAddress();
    if (result?.error) {
      toast.addToast({
        type: "error",
        title: intl.get("stables.depositGenerateErrorTitle"),
        description: result.error,
      });
      return;
    }
    if (!result?.address) return;

    toast.addToast({
      type: "success",
      title: intl.get("stables.depositGenerateSuccessTitle"),
      description: intl.get("stables.depositGenerateSuccessDescription", {
        asset: selectedAsset?.label ?? "",
        network: selectedNetwork?.label ?? "",
      }),
    });
  }, [boundMode, generateAddress, selectedAsset?.label, selectedNetwork?.label, toast]);

  const handleAssetChange = (assetId) => {
    if (lockSelectors) return;
    setSelectedAssetId(assetId);
    setSelectedNetworkId("");
    setNetworkTouched(false);
  };

  const networkLocked = lockSelectors || hasAddress;

  const handleNetworkChange = (networkId) => {
    if (networkLocked) return;
    setNetworkTouched(true);
    setSelectedNetworkId(networkId);
  };

  if (!boundMode && balancesLoading) {
    return <StablesDepositSkeleton />;
  }

  if (!boundMode && balancesError) {
    return (
      <div className="app-empty-state">
        <p>{intl.get("stables.balancesLoadError")}</p>
        <button type="button" className="btn-outline mt-2 !rounded-full !px-4" onClick={reloadBalances}>
          {intl.get("stables.retryLoad")}
        </button>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {introMessage || intl.get("stables.depositIntro")}
      </p>

      <div className="mt-4 space-y-4">
        <StablesAssetSelect
          value={selectedAssetId}
          onChange={handleAssetChange}
          options={assets}
          disabled={lockSelectors}
        />

        {!boundMode && networksLoading ? (
          <div className="space-y-2">
            <Skeleton variant="text" width={40} height={12} />
            <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1, bgcolor: "grey.100" }} />
          </div>
        ) : !boundMode && networksError ? (
          <div className="app-empty-state">
            <p>{networksError}</p>
            <button type="button" className="btn-outline mt-2 !rounded-full !px-4" onClick={reloadNetworks}>
              {intl.get("stables.retryLoad")}
            </button>
          </div>
        ) : (
          <StablesNetworkSelect
            value={selectedNetworkId}
            onChange={handleNetworkChange}
            options={availableNetworks}
            disabled={networkLocked}
          />
        )}
      </div>

      <StablesDepositAddressPanel
        assetLabel={selectedAsset?.label}
        networkLabel={selectedNetwork?.label}
        address={currentAddress}
        hasAddress={hasAddress}
        loading={
          !boundMode &&
          ((loading && !hasAddress) || (!selectedNetworkId && !addressError && !hasAddress))
        }
        error={!boundMode ? addressError : null}
        onRetry={!boundMode ? reloadAddress : undefined}
        onGenerate={!boundMode ? handleGenerateAddress : undefined}
        generating={generating}
        onCopy={hasAddress ? handleCopy : undefined}
        emptyHint={
          emptyAddressMessage ||
          intl.get("stables.depositNoAddressHint", {
            asset: selectedAsset?.label || "—",
            network: selectedNetwork?.label || "—",
          })
        }
      />
    </div>
  );
};
